import { useQuery } from "@tanstack/react-query";
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Award, BookOpen, GraduationCap, Percent, Users } from "lucide-react";
import { api } from "@/lib/api";
import { usePageTitle } from "@/lib/title";
import type { AnalyticsOverview } from "@/types";
import { Card, CardBody, CardHeader } from "@/components/ui/Card";
import { FullPageSpinner } from "@/components/ui/Spinner";

const SERIES_BLUE = "#2a78d6";
const SERIES_ORANGE = "#eb6834";

interface TrendPoint {
  day: string;
  count: number;
}

function StatTile({ icon: Icon, label, value }: { icon: typeof Users; label: string; value: string }) {
  return (
    <Card>
      <CardBody className="flex items-center gap-4">
        <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-brand-50 text-brand-600">
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <p className="text-2xl font-semibold text-slate-900">{value}</p>
          <p className="text-sm text-slate-500">{label}</p>
        </div>
      </CardBody>
    </Card>
  );
}

function TrendTooltip({ active, payload, label }: { active?: boolean; payload?: Array<{ value: number }>; label?: string }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm shadow-md">
      <p className="text-slate-500">{label}</p>
      <p className="font-semibold text-slate-900">{payload[0].value} enrollments</p>
    </div>
  );
}

function CompletionBar({ completed, total }: { completed: number; total: number }) {
  const inProgress = Math.max(total - completed, 0);
  const completedPct = total > 0 ? (completed / total) * 100 : 0;
  const inProgressPct = 100 - completedPct;

  return (
    <div>
      <div className="flex h-3 w-full overflow-hidden rounded-full bg-slate-100">
        {completedPct > 0 && (
          <div className="h-full rounded-l-full" style={{ width: `${completedPct}%`, backgroundColor: SERIES_BLUE }} />
        )}
        {inProgressPct > 0 && (
          <div
            className="h-full rounded-r-full"
            style={{ width: `${inProgressPct}%`, backgroundColor: SERIES_ORANGE, marginLeft: completedPct > 0 ? 2 : 0 }}
          />
        )}
      </div>
      <div className="mt-3 flex gap-6 text-sm">
        <span className="flex items-center gap-2 text-slate-600">
          <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: SERIES_BLUE }} />
          Completed — {completed} ({completedPct.toFixed(0)}%)
        </span>
        <span className="flex items-center gap-2 text-slate-600">
          <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: SERIES_ORANGE }} />
          In progress — {inProgress} ({inProgressPct.toFixed(0)}%)
        </span>
      </div>
    </div>
  );
}

export function AnalyticsPage() {
  usePageTitle("Analytics");
  const { data: overview, isLoading: loadingOverview } = useQuery({
    queryKey: ["analytics", "overview"],
    queryFn: () => api.get<AnalyticsOverview>("/api/v1.0/analytics/overview"),
  });
  const { data: trend, isLoading: loadingTrend } = useQuery({
    queryKey: ["analytics", "enrollment-trend"],
    queryFn: () => api.get<TrendPoint[]>("/api/v1.0/analytics/enrollment-trend"),
  });

  if (loadingOverview || loadingTrend || !overview) return <FullPageSpinner />;

  return (
    <div className="flex flex-col gap-6">
      <p className="-mt-3 text-sm text-slate-500">Platform-wide learning outcomes and engagement.</p>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
        <StatTile icon={Users} label="Students" value={String(overview.total_students)} />
        <StatTile icon={BookOpen} label="Published courses" value={String(overview.published_courses)} />
        <StatTile icon={GraduationCap} label="Enrollments" value={String(overview.total_enrollments)} />
        <StatTile icon={Percent} label="Avg quiz score" value={`${overview.avg_quiz_score.toFixed(0)}%`} />
        <StatTile icon={Award} label="Certificates issued" value={String(overview.certificates_issued)} />
      </div>

      <Card>
        <CardHeader>
          <h2 className="font-semibold text-slate-900">Enrollments — last 30 days</h2>
        </CardHeader>
        <CardBody>
          {!trend?.length ? (
            <p className="py-8 text-center text-sm text-slate-400">No enrollment activity yet.</p>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={trend} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                <defs>
                  <linearGradient id="enrollmentFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={SERIES_BLUE} stopOpacity={0.25} />
                    <stop offset="100%" stopColor={SERIES_BLUE} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid vertical={false} stroke="#e2e8f0" />
                <XAxis
                  dataKey="day"
                  tickFormatter={(d: string) => new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                  tick={{ fontSize: 12, fill: "#94a3b8" }}
                  axisLine={false}
                  tickLine={false}
                  minTickGap={24}
                />
                <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: "#94a3b8" }} axisLine={false} tickLine={false} width={32} />
                <Tooltip content={<TrendTooltip />} />
                <Area type="monotone" dataKey="count" stroke={SERIES_BLUE} strokeWidth={2} fill="url(#enrollmentFill)" />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <h2 className="font-semibold text-slate-900">Completion rate</h2>
        </CardHeader>
        <CardBody>
          <CompletionBar completed={overview.completed_enrollments} total={overview.total_enrollments} />
        </CardBody>
      </Card>
    </div>
  );
}
